import { UISref, useRouter } from "@uirouter/react";
import { noop } from "lodash";
import { useContext, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { boolean, object } from "yup";
import { captcha } from "../../../components/captcha";
import { EditableField } from "../../../components/forms/field";
import { useAppLoader } from "../../../components/loader/app-loader-hook";
import { GlobalOverlayContext } from "../../../components/overlay/context";
import { AuthDTO } from "../../../store/auth/models/dto";
import { AuthService } from "../../../store/auth/service";
import { useYupValidationResolver } from "../../../utils/utils";
import { DashboardRoutes } from "../../dashboard/routes";
import { Form } from "../base";
import { SuccessSignUp } from "./success";

type RequestDTO = Omit<AuthDTO.Requests.CreateIndividualUser.T, "token">;
const { useFields, defaults } = AuthDTO.Requests.CreateIndividualUser;

export const RegisterIndividualPage = () => {
  const { labels, validation } = useFields();
  const { t } = useTranslation("fields");
  const { setOverlay } = useContext(GlobalOverlayContext);

  const resolver = useYupValidationResolver(
    useMemo(
      () =>
        validation.concat(
          object({
            acceptTerms: boolean()
              .required(t("the-terms-and-conditions-must-be-accepted"))
              .isTrue(t("the-terms-and-conditions-must-be-accepted")),
          })
        ),
      [validation]
    )
  );
  const loader = useAppLoader();
  const router = useRouter();

  const {
    control,
    formState: { errors, isValid, isSubmitted },
    handleSubmit,
  } = useForm<RequestDTO & { acceptTerms: boolean }>({
    defaultValues: defaults,
    resolver,
  });

  return (
    <Form.Form
      onSubmit={handleSubmit((data) => {
        loader(
          captcha()
            .then((token) =>
              AuthService.individualSignUp({ ...data, token: token })
            )
            .then(() => router.stateService.go(DashboardRoutes.name))
            .then(() =>
              setOverlay({
                title: "Success",
                children: <SuccessSignUp merchant={false} />,
              })
            )
            .catch(noop) // TODO log such error maybe
        );
      })}
    >
      <h1 className="h1">Sign Up</h1>
      <EditableField editing control={control} name="emailAddress">
        <Form.TextField
          fullWidth
          type="email"
          label={labels.emailAddress}
          error={!!errors.emailAddress}
          helperText={errors.emailAddress?.message}
          autoComplete="username"
        />
      </EditableField>
      <EditableField editing control={control} name="password">
        <Form.PasswordField
          obfuscating
          fullWidth
          autoComplete="new-password"
          label={labels.password}
          error={!!errors.password}
          helperText={errors.password?.message}
        />
      </EditableField>
      <EditableField editing control={control} name="confirmPassword">
        <Form.PasswordField
          obfuscating
          fullWidth
          autoComplete="new-password"
          label={labels.confirmPassword}
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword?.message}
        />
      </EditableField>
      <EditableField editing control={control} name="firstName">
        <Form.TextField
          fullWidth
          label={labels.firstName}
          error={!!errors.firstName}
          helperText={errors.firstName?.message}
          autoComplete="given-name"
        />
      </EditableField>
      <EditableField editing control={control} name="lastName">
        <Form.TextField
          fullWidth
          label={labels.lastName}
          error={!!errors.lastName}
          helperText={errors.lastName?.message}
          autoComplete="family-name"
        />
      </EditableField>
      <EditableField editing control={control} name="dateOfBirth">
        <Form.DateField
          fullWidth
          label={labels.dateOfBirth}
          error={!!errors.dateOfBirth}
          helperText={errors.dateOfBirth?.message}
          autoComplete="bday"
        />
      </EditableField>
      <EditableField editing control={control} name="nationality">
        <Form.Country
          fullWidth
          label={labels.nationality}
          error={!!errors.nationality}
          helperText={errors.nationality?.message}
        />
      </EditableField>
      <EditableField editing control={control} name="acceptTerms">
        <Form.CheckboxField
          label={
            <>
              By signing up I confirm I have read and agree to the{" "}
              <a
                href="https://sekuritance.com/footer/terms-and-conditions/"
                target="_blank"
                rel="noreferrer"
              >
                Terms and Conditions
              </a>
              . Sekuritance sends occasional marketing messages which can be
              switched off. Sekuritance manages personal data as set out in our{" "}
              <a
                href="https://sekuritance.com/footer/sekuritance-privacy-policy/"
                target="_blank"
                rel="noreferrer"
              >
                Privacy policy
              </a>
            </>
          }
          error={!!errors.acceptTerms}
          helperText={errors.acceptTerms?.message}
        />
      </EditableField>
      <Form.Login id="register" disabled={!isValid && isSubmitted}>
        Sign Up
      </Form.Login>
      <Form.Smaller>
        Already have an account?{" "}
        <UISref to="^.login">
          <a>Sign In!</a>
        </UISref>
      </Form.Smaller>
    </Form.Form>
  );
};
